import { useState, useRef, useCallback, useEffect } from 'react';
import { ZoomIn, ZoomOut, Maximize } from 'lucide-react';

const MIN_ZOOM = 1;
const MAX_ZOOM = 8;
const ZOOM_STEP = 0.3;

const DigitalZoom = ({ children }) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const containerRef = useRef(null);
  const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const pinchRef = useRef({ distance: 0, zoom: 1 });
  const lastTapRef = useRef(0);

  // Clamp pan so you can't scroll past the edge of the zoomed content
  const clampPan = useCallback((x, y, z) => {
    if (z <= 1) return { x: 0, y: 0 };
    const maxPan = ((z - 1) / (2 * z)) * 100;
    return {
      x: Math.max(-maxPan, Math.min(maxPan, x)),
      y: Math.max(-maxPan, Math.min(maxPan, y))
    };
  }, []);

  // Reset zoom
  const resetZoom = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Zoom toward a point (mouse position or pinch center)
  const zoomAtPoint = useCallback((newZoom, clientX, clientY) => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    // Position within container as percentage (0-100)
    const pointX = ((clientX - rect.left) / rect.width) * 100;
    const pointY = ((clientY - rect.top) / rect.height) * 100;

    setZoom(prev => {
      const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
      if (clamped <= 1) {
        setPan({ x: 0, y: 0 });
        return 1;
      }

      // Adjust pan to keep the zoom point stable
      setPan(prevPan => {
        const scaleDiff = clamped / prev;
        const newX = prevPan.x * scaleDiff + (pointX - 50) * (1 - scaleDiff);
        const newY = prevPan.y * scaleDiff + (pointY - 50) * (1 - scaleDiff);
        return clampPan(newX, newY, clamped);
      });

      return clamped;
    });
  }, [clampPan]);

  // ── Mouse wheel zoom ──
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    zoomAtPoint(zoom + delta, e.clientX, e.clientY);
  }, [zoom, zoomAtPoint]);

  // ── Mouse drag to pan ──
  const handleMouseDown = useCallback((e) => {
    if (zoom <= 1) return;
    if (e.button !== 0) return; // left click only
    e.preventDefault();
    setIsPanning(true);
    panStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  }, [zoom, pan]);

  const handleMouseMove = useCallback((e) => {
    if (!isPanning) return;
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const dx = ((e.clientX - panStartRef.current.x) / rect.width) * 100;
    const dy = ((e.clientY - panStartRef.current.y) / rect.height) * 100;

    const newPan = clampPan(
      panStartRef.current.panX + dx,
      panStartRef.current.panY + dy,
      zoom
    );
    setPan(newPan);
  }, [isPanning, zoom, clampPan]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // ── Double click to reset ──
  const handleDoubleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (zoom > 1) {
      resetZoom();
    } else {
      zoomAtPoint(3, e.clientX, e.clientY);
    }
  }, [zoom, resetZoom, zoomAtPoint]);

  // ── Touch: pinch zoom + drag pan + double-tap ──
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      // Pinch start
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      pinchRef.current = { distance: dist, zoom };
    } else if (e.touches.length === 1 && zoom > 1) {
      // Pan start
      setIsPanning(true);
      panStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        panX: pan.x,
        panY: pan.y
      };
    }

    // Double-tap detection
    if (e.touches.length === 1) {
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        e.preventDefault();
        if (zoom > 1) {
          resetZoom();
        } else {
          zoomAtPoint(3, e.touches[0].clientX, e.touches[0].clientY);
        }
      }
      lastTapRef.current = now;
    }
  }, [zoom, pan, resetZoom, zoomAtPoint]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2) {
      // Pinch zoom
      e.preventDefault();
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const scale = dist / pinchRef.current.distance;
      const newZoom = pinchRef.current.zoom * scale;
      const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      zoomAtPoint(newZoom, centerX, centerY);
    } else if (e.touches.length === 1 && isPanning && zoom > 1) {
      // Pan
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const dx = ((e.touches[0].clientX - panStartRef.current.x) / rect.width) * 100;
      const dy = ((e.touches[0].clientY - panStartRef.current.y) / rect.height) * 100;

      const newPan = clampPan(
        panStartRef.current.panX + dx,
        panStartRef.current.panY + dy,
        zoom
      );
      setPan(newPan);
    }
  }, [isPanning, zoom, zoomAtPoint, clampPan]);

  const handleTouchEnd = useCallback(() => {
    setIsPanning(false);
    pinchRef.current = { distance: 0, zoom };
  }, [zoom]);

  // Global mouseup listener (in case mouse leaves container while dragging)
  useEffect(() => {
    if (isPanning) {
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('mousemove', handleMouseMove);
      return () => {
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [isPanning, handleMouseUp, handleMouseMove]);

  // Attach wheel listener with passive: false
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const isZoomed = zoom > 1;

  return (
    <div
      ref={containerRef}
      className="digital-zoom-container"
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        cursor: isZoomed ? (isPanning ? 'grabbing' : 'grab') : 'default',
        touchAction: isZoomed ? 'none' : 'auto',
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Zoomed content */}
      <div
        style={{
          width: '100%',
          height: '100%',
          transform: `scale(${zoom}) translate(${pan.x}%, ${pan.y}%)`,
          transformOrigin: 'center center',
          transition: isPanning ? 'none' : 'transform 0.15s ease-out',
        }}
      >
        {children}
      </div>

      {/* Zoom indicator - only show when zoomed */}
      {isZoomed && (
        <div style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(8px)',
          padding: '6px 10px',
          borderRadius: '20px',
          color: 'white',
          fontSize: '12px',
          fontWeight: 600,
          zIndex: 20,
          userSelect: 'none',
        }}>
          <ZoomIn size={14} />
          <span>{zoom.toFixed(1)}x</span>
          <button
            onClick={(e) => { e.stopPropagation(); resetZoom(); }}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '22px',
              height: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              marginLeft: '4px',
              padding: 0,
            }}
            title="Reset zoom"
          >
            <Maximize size={12} />
          </button>
        </div>
      )}

      {/* Zoom controls - bottom right */}
      <div style={{
        position: 'absolute',
        bottom: '80px',
        right: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        zIndex: 20,
        opacity: 0.7,
        transition: 'opacity 0.2s',
      }}
      onMouseEnter={e => e.currentTarget.style.opacity = '1'}
      onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            const rect = containerRef.current.getBoundingClientRect();
            zoomAtPoint(zoom + ZOOM_STEP * 2, rect.left + rect.width / 2, rect.top + rect.height / 2);
          }}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px 8px 0 0',
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            border: 'none',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
          title="Zoom in"
        >
          <ZoomIn size={18} />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            const rect = containerRef.current.getBoundingClientRect();
            zoomAtPoint(zoom - ZOOM_STEP * 2, rect.left + rect.width / 2, rect.top + rect.height / 2);
          }}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '0 0 8px 8px',
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            border: 'none',
            color: zoom <= 1 ? 'rgba(255,255,255,0.3)' : 'white',
            cursor: zoom <= 1 ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
          title="Zoom out"
        >
          <ZoomOut size={18} />
        </button>
      </div>
    </div>
  );
};

export default DigitalZoom;
